import type { CreateBranchFormProps } from './Form';
import type { VersionRepresentationType } from '@readme/api/src/mappings/version/types';

import React from 'react';

import useClassy from '@core/hooks/useClassy';
import { useSuperHubStore } from '@core/store';
import type { EphemeralBranch } from '@core/store/SuperHub/Branches/utils';

import Button from '@ui/Button';
import Flex from '@ui/Flex';
import Icon from '@ui/Icon';
import LazyTippy from '@ui/LazyTippy';

import CreateBranchFormProvider, { useCreateBranchFormContext } from './Context';
import CreateBranchForm from './Form';
import styles from './index.module.scss';

interface CreateBranchProps extends Pick<CreateBranchFormProps, 'onSuccess'> {
  children?: React.ReactNode;
  disabled?: boolean;
  source: EphemeralBranch['source'];
  version: VersionRepresentationType;
}

function Content({ version, children, disabled, onSuccess, source }: CreateBranchProps) {
  const bem = useClassy(styles, 'CreateBranch');

  const formValue = useCreateBranchFormContext();
  const [ephemeralBranch, updateEphemeralBranch] = useSuperHubStore(s => [
    s.branches.ephemeralBranch,
    s.branches.updateEphemeralBranch,
  ]);

  return (
    <LazyTippy
      appendTo={document.body}
      arrow={false}
      content={<CreateBranchForm onSuccess={onSuccess} />}
      interactive
      maxWidth={320}
      offset={[0, 2]}
      onClickOutside={() => {
        if (formValue.formState.isSubmitting) return;
        updateEphemeralBranch(null);
      }}
      placement="bottom-start"
      role="dialog"
      visible={ephemeralBranch?.base === version.name && ephemeralBranch.source === source}
      zIndex={10002} // Must be less than our Modal z-index.
    >
      <Button
        aria-label="Create Branch"
        className={bem('-button')}
        disabled={!!disabled}
        kind="contrast"
        onClick={() => updateEphemeralBranch({ base: version.name, source })}
        size="xs"
        text
        uppercase={false}
      >
        {children || (
          <Flex align="center" gap="0">
            <Icon aria-hidden="true" name="plus" />
            Branch
          </Flex>
        )}
      </Button>
    </LazyTippy>
  );
}

export default function CreateBranch(props: CreateBranchProps) {
  return (
    <CreateBranchFormProvider>
      <Content {...props} />
    </CreateBranchFormProvider>
  );
}
